import React, { useMemo, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { Card } from '../components/ui/Card';
import { PageContainer } from '../components/common/PageContainer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuth } from '../contexts/AuthContext';
import { useTeam } from '../contexts/TeamContext';
import { 
  faClipboardList, 
  faChartLine, 
  faClock,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';

const FullWidthPageContainer = styled(PageContainer)`
  padding: 0 !important;
  gap: 0 !important;
  min-height: 0 !important;
  width: 100% !important;
  max-width: 100% !important;
  flex: 1 !important;
  display: flex !important;
  flex-direction: column !important;
  overflow: visible !important;
  box-sizing: border-box !important;
  justify-content: center;
  align-items: center;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: clamp(var(--spacing-8), 5vw, var(--spacing-12));
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  flex-shrink: 0;
`;

const LogoImage = styled.img`
  width: clamp(24rem, 40vw, 32rem);
  height: auto;
  object-fit: contain;
  background: transparent;
  loading: eager;
  decoding: async;
`;

const CardsSection = styled.section`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: clamp(1.75rem, 4vw, 2.5rem);
  padding: 0 clamp(var(--spacing-6), 6vw, var(--spacing-16));
  flex: 0 1 auto;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: clamp(var(--spacing-6), 3vw, var(--spacing-10));
  width: 100%;
  max-width: 90rem; /* Limit max width for better centering */
  margin: 0 auto; /* Center the grid */
  justify-items: center; /* Center items horizontally within grid */

  @media (max-width: 75rem) { /* 1200px */
    grid-template-columns: repeat(1, minmax(0, 1fr));
    max-width: 100%;
  }
`;

const ActionCard = styled(Card).attrs({ as: 'button' })`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 0; /* Remove gap, spacing handled by individual components */
  width: clamp(20rem, 25vw, 24rem); /* Set width for square */
  aspect-ratio: 1; /* Make it a perfect square */
  padding: clamp(var(--spacing-8), 4vw, var(--spacing-10)) clamp(var(--spacing-6), 4vw, var(--spacing-8)) clamp(var(--spacing-6), 4vw, var(--spacing-8));
  padding-top: clamp(var(--spacing-10), 5vw, var(--spacing-12)); /* Extra top padding to prevent overlap */
  text-align: center;
  border-radius: var(--spacing-8);
  border: 1px solid var(--color-border);
  background: linear-gradient(160deg, var(--color-surface), var(--color-surface-alt));
  overflow: hidden; /* Hide overflow to prevent container expansion */
  cursor: pointer;
  outline: none;
  scroll-behavior: smooth;
  transition: transform 0.3s var(--transition-smooth), box-shadow 0.3s var(--transition-smooth);

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 0.125rem solid var(--color-accent);
    outline-offset: 0.25rem;
    box-shadow: 0 0 0 0.25rem rgba(var(--color-accent-rgb), 0.2);
  }
  
  &:active {
    transform: scale(0.98);
  }

  &::before {
    content: '';
    position: absolute;
    inset: -0.0625rem;
    border-radius: inherit;
    background: linear-gradient(160deg, rgba(var(--color-accent-light-rgb), 0.3), rgba(var(--color-surface-alt-rgb), 0.2));
    opacity: 0.4;
    pointer-events: none;
    transition: opacity 0.35s var(--transition-smooth);
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: radial-gradient(circle at 75% 25%, rgba(var(--color-accent-light-rgb), 0.15), transparent 65%);
    opacity: 0.6;
    mix-blend-mode: screen;
    pointer-events: none;
    transition: opacity 0.35s var(--transition-smooth), transform 0.35s var(--transition-smooth);
  }

  &:hover::before {
    opacity: 0.9;
  }

  &:hover::after {
    opacity: 0.9;
    transform: translateY(-0.5rem);
  }

  @media (max-width: 75rem) { /* 1200px */
    width: clamp(18rem, 30vw, 22rem);
  }

  @media (max-width: 48rem) { /* --breakpoint-tablet */
    width: clamp(16rem, 40vw, 20rem);
    padding: var(--spacing-8);
  }
`;

const CardIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: clamp(4rem, 5vw, 5rem); /* Increased icon size */
  height: clamp(4rem, 5vw, 5rem); /* Increased icon size */
  border-radius: var(--spacing-6);
  background: linear-gradient(160deg, rgba(var(--color-accent-light-rgb), 0.4), rgba(var(--color-accent-rgb), 0.2));
  border: 1px solid var(--color-border);
  font-size: clamp(var(--font-size-xl), 2.5vw, var(--font-size-2xl)); /* Larger icon font */
  color: var(--color-accent);
  box-shadow: 0 var(--spacing-3) var(--spacing-10) rgba(var(--color-accent-rgb), 0.3);
  transform: scale(1);
  transition: transform 0.4s var(--transition-smooth);
  
  ${ActionCard}:hover & {
    transform: scale(1.1); /* Slight scale up on hover */
  }
`;

const CardHeader = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-3); /* Reduced gap between icon and title */
  width: 100%;
  transform: translate(0, 0);
  transition: transform 0.4s var(--transition-smooth);
  
  ${ActionCard}:hover & {
    transform: translateY(-1.25rem); /* Reduced movement to prevent overlap */
  }
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: clamp(var(--font-size-xl), 2.5vw, var(--font-size-2xl)); /* Increased title size */
  font-weight: 600;
  color: var(--color-text-primary);
  text-align: center;
  width: 100%;
  transition: transform 0.4s var(--transition-smooth);
`;

const CardContent = styled.div`
  position: absolute;
  z-index: 3;
  bottom: clamp(var(--spacing-6), 4vw, var(--spacing-8));
  left: clamp(var(--spacing-6), 4vw, var(--spacing-8));
  right: clamp(var(--spacing-6), 4vw, var(--spacing-8));
  display: flex;
  flex-direction: column;
  gap: var(--spacing-3); /* Reduced gap between content items */
  width: auto;
  opacity: 0;
  max-height: 0;
  overflow: hidden;
  transform: translateY(1rem); /* Reduced initial transform */
  align-items: flex-start;
  transition: opacity 0.4s var(--transition-smooth) 0.2s, 
              max-height 0.4s var(--transition-smooth) 0.1s, 
              transform 0.4s var(--transition-smooth) 0.2s;
  
  ${ActionCard}:hover & {
    opacity: 1;
    max-height: 11rem; /* Slightly reduced to fit better */
    transform: translateY(0);
  }
`;

const CardDescription = styled.p`
  margin: 0;
  color: var(--color-text-secondary);
  line-height: 1.65;
  font-size: 1.02rem;
  max-width: 420px;
  text-align: left;
`;

const CardCta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: auto;
  font-size: 0.98rem;
  font-weight: 600;
  color: var(--color-accent-light);
  text-align: left;
  transition: all 0.4s var(--transition-smooth);

  ${ActionCard}:hover & {
    gap: 0.65rem;
    transform: translateX(2px);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-6);
  min-height: 60vh;
  text-align: center;
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  color: var(--color-accent);
  animation: spin 1s linear infinite;

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  font-size: 1.2rem;
  color: var(--color-text-secondary);
  margin: 0;
`;


interface HomeProps {
  onGoToOpponentReports: () => void;
  onGoToUserReports: () => void;
  onGoToLiveGame: () => void;
}

const featureCards = [
  {
    icon: faClipboardList,
    title: 'Pre-Game Preparation',
    description: 'Run opponent scouting reports, dial-in matchups, and surface AI-generated keys to the game.',
    action: 'Open pre-game workspace',
    onClickKey: 'onGoToOpponentReports',
  },
  {
    icon: faChartLine,
    title: 'Team & Player Stats',
    description: 'Digest advanced metrics with sticky headers, column controls, and real-time insights.',
    action: 'Dive into analytics',
    onClickKey: 'onGoToUserReports',
  },
  {
    icon: faClock,
    title: 'Live Game Assistant',
    description: 'Call plays, adjust lineups, and track fatigue with an adaptive sideline copilot.',
    action: 'Enter live assistant',
    onClickKey: 'onGoToLiveGame',
  },
];

export const Home: React.FC<HomeProps> = React.memo(({ onGoToOpponentReports, onGoToUserReports, onGoToLiveGame }) => {
  const { user } = useAuth();
  const { isLoading } = useTeam();
  const handlers = useMemo(() => ({
    onGoToOpponentReports,
    onGoToUserReports,
    onGoToLiveGame,
  }), [onGoToOpponentReports, onGoToUserReports, onGoToLiveGame]) as Record<string, () => void>;

  const handleCardClick = useCallback((onClickKey: string) => {
    const handler = handlers[onClickKey];
    if (handler) {
      handler();
    }
  }, [handlers]);

  // Auto-redirect non-system_admin users to Pre-Game Preparation after loading
  useEffect(() => {
    if (!isLoading && user && user.role !== 'system_admin') {
      // Configuration is loaded, redirect to Pre-Game Preparation
      onGoToOpponentReports();
    }
  }, [isLoading, user, onGoToOpponentReports]);

  // Filter cards based on user role
  const visibleFeatureCards = useMemo(() => {
    return featureCards.filter(card => {
      // Only show "Team & Player Stats" and "Live Game Assistant" cards for system_admin
      if (card.onClickKey === 'onGoToUserReports' || card.onClickKey === 'onGoToLiveGame') {
        return user?.role === 'system_admin';
      }
      return true; // Always show "Pre-Game Preparation"
    });
  }, [user?.role]);

  // Show loader while configuration is loading for non-system_admin users
  if (isLoading && user && user.role !== 'system_admin') {
    return (
      <FullWidthPageContainer>
        <ContentWrapper id="main-content" role="main" aria-label="Loading page">
          <LogoSection>
            <LogoImage 
              src="/logo_and_name_red.png" 
              alt="Vermilion - AI Copilot Assistant" 
              loading="eager"
              width="512"
              height="128"
            />
          </LogoSection>
          
          <LoadingContainer>
            <LoadingSpinner>
              <FontAwesomeIcon icon={faSpinner} />
            </LoadingSpinner>
            <LoadingText>Loading your workspace...</LoadingText>
          </LoadingContainer>
        </ContentWrapper>
      </FullWidthPageContainer>
    );
  }

  return (
    <FullWidthPageContainer>
      <ContentWrapper id="main-content" role="main" aria-label="Home page">
        <LogoSection>
          <LogoImage 
            src="/logo_and_name_red.png" 
            alt="Vermilion - AI Copilot Assistant" 
            loading="eager"
            width="512"
            height="128"
          />
        </LogoSection>

        <CardsSection aria-label="Feature cards">
          <CardsGrid role="list" aria-label="Application features">
            {visibleFeatureCards.map((card, index) => (
              <ActionCard
                key={card.title}
                onClick={() => handleCardClick(card.onClickKey)}
                interactive
                type="button"
                data-track-event="home_card_click"
                data-track-label={card.title}
                data-track-detail={card.onClickKey}
                aria-label={`${card.title}. ${card.description}`}
                aria-describedby={`card-description-${index}`}
                role="listitem"
              >
                <CardHeader>
                  <CardIcon aria-hidden="true">
                    <FontAwesomeIcon icon={card.icon} />
                  </CardIcon>
                  <CardTitle>{card.title}</CardTitle>
                </CardHeader>
                <CardContent id={`card-description-${index}`}>
                  <CardDescription>{card.description}</CardDescription>
                  <CardCta>
                    {card.action}
                    <span aria-hidden="true">➜</span>
                  </CardCta>
                </CardContent>
              </ActionCard>
            ))}
          </CardsGrid>
        </CardsSection>
      </ContentWrapper>
    </FullWidthPageContainer>
  );
});

Home.displayName = 'Home';
